import { useId, useState, type FormEvent } from 'react'
import { Button, Row, Table, TableScroll, Td, Th } from '../../ui'
import { addExemptAccount, getExemptAccounts, removeExemptAccount } from '../../lib/api'
import { formatDate } from './LiveFindings'
import { describeFailure, useApi } from './useApi'
import { MoreAbout, SettingsSection } from './SettingsSection'
import './live.css'

/**
 * Emergency (break-glass) and service accounts (migration 0154).
 *
 * CISA's baseline allows a named emergency account to be left out of Conditional Access and to keep
 * standing Global Administrator access, and a service account that cannot activate roles to hold one
 * permanently. Defenssive cannot tell which accounts those are, so the owner says, here -- and the
 * checks accept exactly these and name them in their evidence. Nothing else is accepted: an account
 * left out of a policy that is not on this list keeps the finding open (owner's decision,
 * 2026-09-25).
 *
 * Everyone can read the list, because it explains findings they read. Only the owner can change it,
 * and the server decides that -- the controls are hidden from others only so nobody is offered a
 * button that will be refused.
 */
export function ExemptAccountsCard({ isOwner }: { isOwner: boolean }) {
  const [refresh, setRefresh] = useState(0)
  const list = useApi(() => getExemptAccounts(), [refresh], undefined, true)
  const reload = () => setRefresh((n) => n + 1)

  const [removing, setRemoving] = useState<string | null>(null)
  const [removeError, setRemoveError] = useState<string | null>(null)

  async function remove(id: string) {
    setRemoving(id)
    setRemoveError(null)
    const result = await removeExemptAccount(id)
    setRemoving(null)
    if (result.ok) {
      reload()
      return
    }
    // 404: somebody (or another tab) already removed it. It is gone, which is what was asked, and
    // the row on screen is stale -- so show the server's list rather than an error that repeats on
    // every retry. The other refusals also mean this view is out of date.
    if (result.status === 404) {
      reload()
      return
    }
    setRemoveError(result.message)
    if (result.status === 401 || result.status === 403 || result.status === 409) reload()
  }

  return (
    <SettingsSection
      title="Emergency and service accounts"
      description={
        <p>
          Microsoft recommends two or more emergency ("break-glass") accounts that stay outside
          Conditional Access and keep standing Global Administrator access, for when normal sign-in
          fails. List them here, with any service accounts that cannot use MFA or Privileged Identity
          Management.
        </p>
      }
    >
      <MoreAbout label="What the checks accept for a listed account">
      <p className="settings-note">
        What Defenssive's checks then accept: a listed account that a Conditional Access policy
        excludes by name (one by one, not through a group, which keeps the finding open whoever is
        in it), a listed account's permanent admin role, and any admin role held by a listed guest or
        external account. A listed service account is also not counted as missing MFA; an emergency
        account still needs an MFA method, so it stays in that finding. Owning an application or a
        group that holds an admin role is never accepted. Any finding a listed account appears in
        names it. Any other account left out of a policy keeps its finding open.
      </p>
      </MoreAbout>

      {list.state === 'loading' ? (
        <p className="live-empty">Loading…</p>
      ) : list.state === 'error' ? (
        <p className="live-error">{describeFailure(list.status)}</p>
      ) : list.data.accounts.length === 0 ? (
        <p className="settings-empty">No accounts are listed, so no exclusion is accepted.</p>
      ) : (
        <TableScroll label="Emergency and service accounts">
          <Table>
            <thead>
              <Row>
                <Th>Account</Th>
                <Th>Type</Th>
                <Th>Why</Th>
                <Th>Added</Th>
                {isOwner ? <Th>{''}</Th> : null}
              </Row>
            </thead>
            <tbody>
              {list.data.accounts.map((a) => (
                <Row key={a.id}>
                  <Td>
                    {a.userPrincipalName}
                    {a.displayName ? <span className="finding-row-summary">{a.displayName}</span> : null}
                  </Td>
                  <Td>{a.kind === 'emergency' ? 'Emergency access' : a.kind === 'service' ? 'Service account' : a.kind}</Td>
                  <Td>{a.reason}</Td>
                  <Td>
                    {formatDate(a.addedAt)}
                    {a.addedBy ? <span className="finding-row-summary">by {a.addedBy}</span> : null}
                  </Td>
                  {isOwner ? (
                    <Td>
                      <Button
                        variant="ghost"
                        disabled={removing !== null}
                        onClick={() => void remove(a.id)}
                        aria-label={`Remove ${a.userPrincipalName}`}
                      >
                        {removing === a.id ? 'Removing…' : 'Remove'}
                      </Button>
                    </Td>
                  ) : null}
                </Row>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      {removeError ? (
        <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
          {removeError}
        </p>
      ) : null}

      {isOwner ? (
        <AddExemptAccount onAdded={reload} />
      ) : (
        <p className="settings-note">Only the workspace owner can change this list.</p>
      )}
    </SettingsSection>
  )
}

function AddExemptAccount({ onAdded }: { onAdded: () => void }) {
  const ids = { address: useId(), kind: useId(), reason: useId() }
  const [address, setAddress] = useState('')
  const [kind, setKind] = useState<'emergency' | 'service'>('emergency')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [added, setAdded] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setAdded(null)
    if (reason.trim().length < 3) {
      setError('Say why this account is an exception.')
      return
    }
    setBusy(true)
    const result = await addExemptAccount({ userPrincipalName: address.trim(), kind, reason: reason.trim() })
    setBusy(false)
    if (!result.ok) {
      setError(result.message)
      return
    }
    setAdded(address.trim())
    setAddress('')
    setReason('')
    onAdded()
  }

  return (
    <form className="invite-form settings-form-grid" onSubmit={submit} style={{ marginTop: 'var(--space-5)' }}>
      <h3 className="settings-subheading">Add an account</h3>
      <label className="invite-field" htmlFor={ids.address}>
        <span className="invite-label">Sign-in address</span>
        <input
          id={ids.address}
          className="findings-search-input"
          type="email"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="breakglass@yourcompany.com"
          autoComplete="off"
          spellCheck={false}
          required
        />
      </label>

      <label className="invite-field" htmlFor={ids.kind}>
        <span className="invite-label">Type</span>
        <select
          id={ids.kind}
          className="findings-search-input"
          value={kind}
          onChange={(e) => setKind(e.target.value === 'service' ? 'service' : 'emergency')}
        >
          <option value="emergency">Emergency access (break-glass)</option>
          <option value="service">Service account</option>
        </select>
      </label>

      <label className="invite-field settings-field-wide" htmlFor={ids.reason}>
        <span className="invite-label">Why is it an exception?</span>
        <input
          id={ids.reason}
          className="findings-search-input"
          type="text"
          maxLength={500}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Emergency access; FIDO2 key in the office safe"
        />
      </label>

      <div style={{ marginTop: 'var(--space-3)' }}>
        <Button type="submit" variant="primary" disabled={busy || address.trim() === ''}>
          {busy ? 'Adding…' : 'Add account'}
        </Button>
      </div>

      {error ? (
        <p className="live-error" role="alert" style={{ marginTop: 'var(--space-3)' }}>
          {error}
        </p>
      ) : null}
      {added ? (
        <p className="settings-note" role="status">
          Added {added}. The next assessment takes it into account.
        </p>
      ) : null}
    </form>
  )
}
